// ponytail: [cynos-sociomanch payment bridge] - SocioManch branded, zero Cynos visibility
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

const SOCIOMANCH_PURPLE = "#7c3aed";
const SOCIOMANCH_INDIGO = "#4f46e5";

const styles = {
  root: {
    margin: 0,
    padding: 0,
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f0f1a 0%, #1a1033 50%, #0d1b2a 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
  },
  // Animated background orbs
  orb1: {
    position: "fixed",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)",
    top: "-100px",
    left: "-100px",
    pointerEvents: "none",
  },
  orb2: {
    position: "fixed",
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(79,70,229,0.12) 0%, transparent 70%)",
    bottom: "-50px",
    right: "-50px",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    background: "rgba(255,255,255,0.04)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: "48px 40px",
    maxWidth: 460,
    width: "90%",
    textAlign: "center",
    boxShadow: "0 32px 64px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 32,
  },
  logoMark: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    boxShadow: "0 8px 24px rgba(124,58,237,0.4)",
  },
  logoText: {
    fontSize: 22,
    fontWeight: 800,
    background: "linear-gradient(135deg, #a78bfa, #818cf8)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: "-0.5px",
  },
  spinner: {
    width: 56,
    height: 56,
    margin: "0 auto 24px",
    border: "3px solid rgba(124,58,237,0.2)",
    borderTop: "3px solid #7c3aed",
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  },
  errorIcon: {
    width: 56,
    height: 56,
    margin: "0 auto 24px",
    background: "rgba(239,68,68,0.12)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    border: "1px solid rgba(239,68,68,0.3)",
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 8,
    color: "#f1f5f9",
    letterSpacing: "-0.3px",
  },
  subtitle: {
    fontSize: 14,
    color: "rgba(255,255,255,0.5)",
    lineHeight: 1.6,
    marginBottom: 24,
  },
  securityBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 20,
    background: "rgba(124,58,237,0.1)",
    border: "1px solid rgba(124,58,237,0.25)",
    color: "#a78bfa",
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 24,
  },
  returnBtn: {
    display: "inline-block",
    marginTop: 8,
    padding: "12px 28px",
    borderRadius: 12,
    background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    textDecoration: "none",
    cursor: "pointer",
    border: "none",
    boxShadow: "0 8px 24px rgba(124,58,237,0.35)",
    transition: "all 0.2s ease",
  },
  stepsRow: {
    display: "flex",
    justifyContent: "center",
    gap: 4,
    marginTop: 28,
  },
  stepDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
  },
  stepDotActive: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#7c3aed",
  },
  poweredBy: {
    marginTop: 32,
    fontSize: 11,
    color: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
};

// Inject keyframe animation once
if (typeof document !== "undefined" && !document.getElementById("sm-bridge-styles")) {
  const style = document.createElement("style");
  style.id = "sm-bridge-styles";
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
    @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    #sm-payment-bridge-card { animation: fadeIn 0.4s ease forwards; }
  `;
  document.head.appendChild(style);
}

const STEPS = [
  "Verifying plan details...",
  "Connecting to PhonePe gateway...",
  "Opening secure payment session...",
];

export default function PaymentBridge() {
  const location = useLocation();
  const [status, setStatus] = useState(STEPS[0]);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState("");

  // Cycle through loading steps for UX feedback
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % STEPS.length;
      setStepIndex(i);
      setStatus(STEPS[i]);
    }, 900);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get("order_id");
    const amount = params.get("amount");
    const plan = params.get("plan") || "growth";
    const returnUrl = params.get("return_url") || "https://sociomanch.com/billing";

    if (!orderId || !amount) {
      setError("Missing payment details. Please go back to SocioManch and try again.");
      return;
    }

    api
      .post("/payments/bridge/initiate", {
        order_id: orderId,
        amount: Number(amount),
        plan,
        return_url: returnUrl,
      })
      .then((res) => {
        const payUrl = res.data?.payment_url || res.data?.redirect_url;
        if (payUrl) {
          window.location.href = payUrl;
        } else {
          setError("Payment gateway did not return a checkout link. Please try again.");
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || "Failed to connect to payment gateway. Please try again.";
        setError(msg);
      });
  }, [location.search]);

  const returnUrl = new URLSearchParams(location.search).get("return_url") || "https://sociomanch.com/billing";

  return (
    <>
      {/* Override any global layout from Cynos */}
      <style>{`
        body { margin: 0 !important; padding: 0 !important; background: #0f0f1a !important; }
        #root > div.App { min-height: 100vh; background: transparent; }
      `}</style>

      <div style={styles.root}>
        <div style={styles.orb1} />
        <div style={styles.orb2} />

        <div id="sm-payment-bridge-card" style={styles.card}>
          {/* SocioManch Logo */}
          <div style={styles.logo}>
            <div style={styles.logoMark}>⚡</div>
            <span style={styles.logoText}>SocioManch</span>
          </div>

          {error ? (
            <>
              <div style={styles.errorIcon}>⚠️</div>
              <h1 style={{ ...styles.title, color: "#fca5a5" }}>Payment Initiation Failed</h1>
              <p style={styles.subtitle}>{error}</p>
              <a
                href={returnUrl + (returnUrl.includes("?") ? "&" : "?") + "payment=failed&error=" + encodeURIComponent(error)}
                style={styles.returnBtn}
                onMouseEnter={(e) => { e.target.style.opacity = "0.85"; }}
                onMouseLeave={(e) => { e.target.style.opacity = "1"; }}
              >
                ← Return to SocioManch
              </a>
            </>
          ) : (
            <>
              <div style={styles.spinner} />
              <h1 style={styles.title}>Connecting to PhonePe</h1>
              <p style={styles.subtitle}>{status}</p>

              <div style={styles.securityBadge}>
                <span>🔒</span>
                <span>256-bit SSL secured · Official PhonePe gateway</span>
              </div>

              {/* Step progress dots */}
              <div style={styles.stepsRow}>
                {STEPS.map((_, i) => (
                  <div key={i} style={i === stepIndex ? styles.stepDotActive : styles.stepDot} />
                ))}
              </div>
            </>
          )}

          <div style={styles.poweredBy}>
            <span>Powered by SocioManch · Secured by PhonePe</span>
          </div>
        </div>
      </div>
    </>
  );
}
